import _ from "underscore";
import PropTypes from "prop-types";
import React from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import OutsideClick from "../elements/outside-click";
import BasicUtils from "../../utils/basic-utils";

const ARROW_BORDER_SIZE = 1;
const OVERFLOW_MARGIN = 10;
const _titleize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export default class Overlay extends React.Component {
  static displayName = "Overlay";

  static propTypes = {
    position: PropTypes.oneOf([
      "top",
      "bottom",
      "left",
      "right",
      "bottom-right-aligned",
      "bottom-left-aligned",
      "left-top-aligned",
      "right-top-aligned",
    ]),
    width: PropTypes.number,
    height: PropTypes.number,
    origin: PropTypes.object,
    arrowSize: PropTypes.number,
    arrowColor: PropTypes.string,
    arrowBorderColor: PropTypes.string,
    arrowPosition: PropTypes.object,
    onClose: PropTypes.func,
  };

  static defaultProps = {
    position: "bottom",
    origin: { width: 175, height: 10, top: 0, left: 0 },
    arrowSize: 11,
    arrowColor: "#fff",
    arrowBorderColor: "#d2dde0",
  };

  state = {
    height: 100,
    width: this.props.width || 175,
    content_position: {},
    arrow_position: {},
    arrow_position_adjusted: {},
  };

  componentDidMount() {
    const content = ReactDOM.findDOMNode(this.bodyEl);
    this.getContentPosition(content.offsetWidth, content.offsetHeight);
    return this.getArrowPosition();
  }

  handleOutsideClick = () => {
    return typeof this.props.onClose === "function" ? this.props.onClose() : undefined;
  };

  getArrowPosition = () => {
    const { width, height } = this.props.origin;
    const size = this.props.arrowSize;
    const pos = this.props.position.split("-")[0];

    const position = (() => {
      switch (pos) {
        case "top":
          return { left: width / 2 - size, top: -size };
        case "bottom":
          return { left: width / 2 - size, top: height };
        case "right":
          return { left: width, top: height / 2 - size };
        case "left":
          return { left: -size, top: height / 2 - size };
      }
    })();
    const arrow_position = _.extend({ left: position.left, top: position.top }, this.props.arrowPosition);
    this.setState({ arrow_position });
    return this.getArrowBorderAdjust(arrow_position);
  };

  getArrowBorderAdjust = (arrow_position) => {
    const pos = this.props.position.split("-")[0];
    const arrow_position_adjusted = (() => {
      switch (pos) {
        case "top":
          return { top: arrow_position.top - ARROW_BORDER_SIZE };
        case "bottom":
          return { top: arrow_position.top + ARROW_BORDER_SIZE };
        case "left":
          return { left: arrow_position.left - ARROW_BORDER_SIZE };
        case "right":
          return { left: arrow_position.left + ARROW_BORDER_SIZE };
      }
    })();
    return this.setState({ arrow_position_adjusted });
  };

  getContentPosition = (width, height) => {
    const { origin } = this.props;
    width = this.props.width || width; // width of the popover content
    height = this.props.height || height;
    const arrow = this.props.arrowSize;
    const top_aligned_margin = -10; // to position the arrow a little below the top of the popover

    const position = (() => {
      switch (this.props.position) {
        case "top":
          return { left: origin.width / 2 - width / 2, top: -(height + arrow) };
        case "bottom":
          return {
            left: origin.width / 2 - width / 2,
            top: origin.height + arrow,
          };
        case "right":
          return {
            left: origin.width + arrow,
            top: origin.height / 2 - height / 2,
          };
        case "left":
          return {
            left: -(width + arrow),
            top: origin.height / 2 - height / 2,
          };
        case "bottom-left-aligned":
          return { left: 0, top: origin.height + arrow };
        case "bottom-right-aligned":
          return { left: -width + origin.width, top: origin.height + arrow };
        case "left-top-aligned":
          return { left: -(width + arrow), top: top_aligned_margin };
        case "right-top-aligned":
          return { left: origin.width + arrow, top: top_aligned_margin };
      }
    })();

    // Adjust Content container for window overflow
    if (["top", "bottom"].includes(this.props.position)) {
      const window_width = window.outerWidth;
      const offset_left = this.props.origin.left + position.left;
      const offset_right = this.props.origin.left + position.left + width;

      if (offset_left < 0) {
        position.left += Math.abs(offset_left) + OVERFLOW_MARGIN;
      } else if (offset_right > window_width) {
        position.left -= offset_right - window_width + OVERFLOW_MARGIN;
      }
    } else if (["left", "right"].includes(this.props.position)) {
      const window_height = window.outerHeight;
      const offset_top = this.props.origin.top + position.top;
      const offset_bottom = this.props.origin.top + position.top + height;

      if (offset_top < 0) {
        position.top += Math.abs(offset_top) + OVERFLOW_MARGIN;
      } else if (offset_bottom > window_height) {
        position.top -= offset_bottom - window_height + OVERFLOW_MARGIN;
      }
    }

    return this.setState({
      content_position: { top: position.top, left: position.left },
    });
  };

  render() {
    const position = this.props.position.split("-")[0];

    return (
      <div className={classNames("et--overlay", this.props.className)}>
        <OutsideClick
          ref={(el) => {
            return (this.bodyEl = el);
          }}
          className="et--overlay--content"
          onOutsideClick={this.handleOutsideClick}
          style={this.state.content_position}
        >
          {this.props.children}
        </OutsideClick>
        {this.props.arrowSize > 0 ? (
          <div>
            <div
              className={`et--overlay--arrow is-${position}`}
              style={_.extend(
                {},
                this.state.arrow_position,
                { borderWidth: this.props.arrowSize },
                BasicUtils.toObject(`border${_titleize(position)}Color`, this.props.arrowBorderColor)
              )}
            />
            <div
              className={`et--overlay--arrow is-${position}`}
              style={_.extend(
                {},
                this.state.arrow_position,
                this.state.arrow_position_adjusted,
                { borderWidth: this.props.arrowSize },
                BasicUtils.toObject(`border${_titleize(position)}Color`, this.props.arrowColor)
              )}
            />
          </div>
        ) : undefined}
      </div>
    );
  }
}
